import { useEffect, useMemo, useRef, useState } from "react";
import { ExternalLink, Loader2, TrendingDown, TrendingUp } from "lucide-react";
import { CandleChart } from "./CandleChart";

const inr = (v, d = 2) => v.toLocaleString("en-IN", { minimumFractionDigits: d, maximumFractionDigits: d });
const growwUrl = d => d.groww_slug ? `https://groww.in/stocks/${d.groww_slug}` : `https://groww.in/search?q=${encodeURIComponent(d.symbol)}`;
const SOURCE = { GROWW_STREAM: "LIVE · Groww stream", GROWW_POLL: "LIVE · Groww 1s" };

const OneClickBar = ({ data, price, onOrder }) => {
  const [qty, setQty] = useState(1); const [busy, setBusy] = useState("");
  const fire = async side => { window.open(growwUrl(data), "_blank", "noopener"); setBusy(side); await onOrder({ symbol: data.symbol, side, quantity: qty, channel: "GROWW_WEB" }); setBusy(""); };
  return <div className="one-click">
    <label className="qty">Qty<input type="number" min="1" value={qty} data-testid="qty-input" onChange={e => setQty(Math.max(1, Number(e.target.value) || 1))} /></label>
    {["BUY", "SELL"].map(side => <button key={side} className={`oc-btn ${side.toLowerCase()}`} disabled={!!busy} data-testid={`${side.toLowerCase()}-button`} onClick={() => fire(side)}>
      {busy === side ? <Loader2 size={16} className="spin" /> : side === "BUY" ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
      <span><b>{side} {qty} on Groww</b><small>@ ₹{inr(price)} · ₹{inr(qty * price, 0)}</small></span><ExternalLink size={14} className="oc-ext" />
    </button>)}
  </div>;
};

const useFlash = (price, ts) => {
  const prev = useRef(price); const [dir, setDir] = useState("");
  useEffect(() => { if (price !== prev.current) { setDir(price > prev.current ? "up" : "down"); prev.current = price; } }, [price, ts]);
  return dir;
};

const Stats = ({ candles, ind }) => {
  const s = [["Open", inr(candles[0].open)], ["High", inr(Math.max(...candles.map(c => c.high)))], ["Low", inr(Math.min(...candles.map(c => c.low)))],
    ["Volume", candles.reduce((a, c) => a + c.volume, 0).toLocaleString("en-IN")], ["VWAP", inr(ind.vwap)], ["RSI 14", ind.rsi]];
  return <div className="stats">{s.map(([k, v]) => <div key={k}><span>{k}</span><b>{v}</b></div>)}</div>;
};

export const StockView = ({ data, error, tick, onOrder }) => {
  const candles = useMemo(() => {
    if (!data || !tick) return data?.candles;
    const c = [...data.candles], last = { ...c[c.length - 1] };
    Object.assign(last, { close: tick.ltp, high: Math.max(last.high, tick.ltp), low: Math.min(last.low, tick.ltp) });
    c[c.length - 1] = last; return c;
  }, [data, tick]);
  const price = tick?.ltp ?? data?.quote.price;
  const flash = useFlash(price, tick?.ts);
  if (!data) return <section className="stock loading" data-testid="stock-view">{error ? <div className="feed-error" data-testid="stock-data-error">{error}</div> : <><Loader2 className="spin" /> Loading live Groww chart…</>}</section>;
  const change = price - data.candles[0].open, pct = (change / data.candles[0].open) * 100, up = change >= 0;
  const sig = data.latest_signal;
  return <section className="stock" data-testid="stock-view">
    <div className="stock-head">
      <div>
        <div className="stock-name"><span data-testid="stock-symbol">{data.symbol}</span> <small>{data.name} · NSE</small></div>
        <div className="price-row">
          <span key={tick?.ts} className={`px flash-${flash}`} data-testid="live-price">₹{inr(price)}</span>
          <span className={`chg ${up ? "positive" : "negative"}`} data-testid="price-change">{up ? "+" : ""}{inr(change)} ({up ? "+" : ""}{pct.toFixed(2)}%)</span>
        </div>
      </div>
      <div className={`live-chip ${tick ? "real" : ""}`} data-testid="live-source"><span className="live-dot" />{tick ? SOURCE[tick.source] : "Connecting…"}<small>candles: {data.data_source} · {data.interval}</small></div>
    </div>
    {tick?.error && <div className="feed-error" data-testid="live-feed-error">{tick.error}</div>}
    <Stats candles={candles} ind={data.indicators} />
    {sig ? <div className={`sig-line ${sig.type.toLowerCase()}`} data-testid="latest-signal"><b>{sig.type} @ ₹{inr(sig.price)}</b><span>{sig.time} IST · strength {sig.strength}%</span>
      {data.levels && <span className="lv">Target ₹{data.levels.target} · Stop-loss ₹{data.levels.stop_loss}</span>}</div>
      : <div className="sig-line" data-testid="latest-signal">No signal yet — waiting for EMA/VWAP confirmation</div>}
    <CandleChart testId="stock-chart" candles={candles} signals={data.signals} levels={data.levels} overlays={data.overlays} live={!!tick} />
    <div className="legend"><span><i className="lg-ema" />EMA 9</span><span><i className="lg-vwap" />VWAP</span><span><i className="lg-buy" />BUY</span><span><i className="lg-sell" />SELL</span><span className="muted">Dotted lines = entry / target / stop-loss</span></div>
    <OneClickBar data={data} price={price} onOrder={onOrder} />
  </section>;
};
